/**
 * editRole — RPC contract for the editRole callable.
 *
 * `role.entity.ts` marks `update` `.requiresHandler('editRole')`.
 * `editRole.api.ts` (onCall): caller holds `tickets.roles.manage` → target
 * role exists AND is not built-in (the seeded `owner` doc stays structurally
 * immutable — the guard the removed Firestore rule's `isBuiltIn.eq(false)`
 * used to carry) → when `patch.permissions` is supplied, it must be ⊆ the
 * caller's own role's permissions → update.
 *
 * Fields justified:
 *   roleId — the role being edited.
 *   patch  — partial: a rename, a permission-set change, or both. Both
 *            fields nullish rather than optional — Firestore/JSON round-trip
 *            `null` for an absent value the same way an omitted key does.
 */
import { z } from "zod";
import { PermissionSchema } from "./permissions.ts";
import { Id } from "./workspace.entity.ts";

export const EditRoleInput = z
	.object({
		orgId: Id,
		roleId: Id,
		patch: z.object({
			name: z.string().min(1).nullish(),
			permissions: z
				.array(PermissionSchema)
				.min(1)
				.nullish()
				.describe(
					"when present, must be ⊆ the caller's own role's permissions",
				),
		}),
	})
	.strict();
export type EditRoleInput = z.infer<typeof EditRoleInput>;

export const EditRoleOutput = z.object({ ok: z.literal(true) }).strict();
export type EditRoleOutput = z.infer<typeof EditRoleOutput>;
